import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { LuPanelRight } from "react-icons/lu";
import { MapPin, Pencil } from "lucide-react";
import { IoFlashOutline } from "react-icons/io5";
import SmartAssignModal from "./SmartAssignModal";
import { getPermissionByScreenCode } from "../../utils/permissions";
import ActionIconButton from "../common/ActionIconButton";
import Tooltip from "../common/Tooltip";

const MapViewToolbar = ({
  onPrint,
  onRefresh,
  onSelect,
  onEdit,
  onCalendar,
  onChart,
  onMarkerSettings,
  onDownload,
  onFullScreen,
  onToggleDetailPanel,
  isDetailPanelOpen,
  onToggleBottomPanel,
  isBottomPanelOpen,
  isBottomPanelExpanded,
  showDatePanel,
  drawingEnabled,
  showCustomMarkerModal,
  isRtl = false,
  isMobileViewport = false,
}) => {
  const { langMode } = useContext(AppContext);
  const [showSmartAssignModal, setShowSmartAssignModal] = useState(false);
  const [desktopToolbarTop, setDesktopToolbarTop] = useState(220);
  const [desktopRailOffset, setDesktopRailOffset] = useState(16);
  const t = translations[langMode];
  const canUpdateTask = Boolean(getPermissionByScreenCode("TASK_MANAGEMENT")?.canUpdate);

  const iconButtons = useMemo(() => [
    {
      icon: IoFlashOutline,
      onClick: canUpdateTask ? () => setShowSmartAssignModal(true) : undefined,
      title: t.smartAssign,
      id: "smart-assign",
      disabled: !canUpdateTask
    },
    // {
    //   icon: BsCursor,
    //   onClick: onSelect,
    //   title: "Muti Select",
    //   id: "select"
    // },
    {
      icon: Pencil,
      onClick: onEdit,
      title: t.drawOnMap,
      id: "edit",
      isActive: drawingEnabled
    },
    {
      icon: MapPin,
      onClick: onMarkerSettings,
      title: t.custommarker,
      id: "custom-marker",
      isActive: showCustomMarkerModal
    },
    // {
    //   icon: LuPanelRight,
    //   onClick: onToggleDetailPanel,
    //   title: t.detailPanel,
    //   id: "detail-panel",
    //   isActive: isDetailPanelOpen
    // }
  ], [t, drawingEnabled, showCustomMarkerModal, showDatePanel, isMobileViewport, isBottomPanelOpen, isDetailPanelOpen, onEdit, onCalendar, onToggleBottomPanel, onChart, onMarkerSettings, onDownload, onFullScreen, onToggleDetailPanel, canUpdateTask]);


  const toolbarBottomGap = 32;
  const detailPanelOffset = 400;
  const rightRailOffset = 16;

  const updateDesktopToolbarPosition = useCallback(() => {
    if (isMobileViewport) return;

    const mapEl = document.getElementById("map");
    if (!mapEl) return;

    const mapRect = mapEl.getBoundingClientRect();
    const trackingEl = mapEl.querySelector(".map-tracking-anchor");
    const gapBelowTracking = 12;

    // The rail sits directly below the Tracking button on this same side -
    // the map type/zoom control lives on the opposite side now, so it no
    // longer factors into this rail's height/position at all.
    const computedTop = trackingEl
      ? Math.max(12, Math.round(trackingEl.getBoundingClientRect().bottom - mapRect.top + gapBelowTracking))
      : (showDatePanel ? 244 : 164);

    setDesktopRailOffset(rightRailOffset);
    setDesktopToolbarTop(computedTop);
  }, [isMobileViewport, showDatePanel, rightRailOffset, langMode]);

  useEffect(() => {
    updateDesktopToolbarPosition();
    if (isMobileViewport) return undefined;

    const timeoutIds = [
      window.setTimeout(updateDesktopToolbarPosition, 0),
      window.setTimeout(updateDesktopToolbarPosition, 180),
      window.setTimeout(updateDesktopToolbarPosition, 360),
    ];

    const onResize = () => updateDesktopToolbarPosition();
    window.addEventListener("resize", onResize);

    const mapEl = document.getElementById("map");
    let resizeObserver = null;
    if (mapEl && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateDesktopToolbarPosition();
      });
      resizeObserver.observe(mapEl);
      const trackingEl = mapEl.querySelector(".map-tracking-anchor");
      if (trackingEl) {
        resizeObserver.observe(trackingEl);
      }
    }

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", onResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [isMobileViewport, updateDesktopToolbarPosition]);

  const getBottomPosition = () => {
    if (showDatePanel && isBottomPanelOpen) {
      return isBottomPanelExpanded ? `${250 + toolbarBottomGap}px` : `${50 + toolbarBottomGap}px`;
    }
    // if (showDatePanel) {
    //   return "124px";
    // }
    if (isBottomPanelOpen) {
      return isBottomPanelExpanded ? `${250 + toolbarBottomGap}px` : `${50 + toolbarBottomGap}px`;
    }
    return `${toolbarBottomGap}px`;
  };

  return (
    <div
      className="map-view-toolbar absolute z-[1000] max-w-[calc(100%_-_24px)] transition-all duration-300"
      style={(() => {
        if (!isMobileViewport) {
          const edgeOffset = `${(isDetailPanelOpen ? detailPanelOffset : 0) + desktopRailOffset}px`;
          return isRtl
            ? { top: `${desktopToolbarTop}px`, left: edgeOffset, right: "auto" }
            : { top: `${desktopToolbarTop}px`, right: edgeOffset, left: "auto" };
        }

        const mobileEdgeOffset = `${(isDetailPanelOpen ? detailPanelOffset : 0) + 84}px`;
        return isRtl
          ? { bottom: getBottomPosition(), left: mobileEdgeOffset, right: "auto" }
          : { bottom: getBottomPosition(), right: mobileEdgeOffset, left: "auto" };
      })()}
    >
      <div
        className={
          isMobileViewport
            ? "flex flex-wrap items-center justify-end gap-2 rounded-xl border border-[#d7dfea] bg-white/95 p-2 shadow-md backdrop-blur-sm"
            : "map-right-rail-container flex flex-col items-center gap-0.5"
        }
      >
        {iconButtons.map((button, index) => {
          const IconComponent = button.icon;
          const isLast = index === iconButtons.length - 1;
          return (
            <div key={button.id} className="relative flex flex-col items-center">
              <Tooltip
                text={button.title}
                position="left"
                disabled={
                  isMobileViewport ||
                  button.isActive ||
                  (button.id === "smart-assign" && showSmartAssignModal)
                }
              >
                <ActionIconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    button.onClick?.();
                  }}
                  disabled={button.disabled}
                  aria-label={button.title}
                  tone="gray"
                  data-map-toolbar-button-id={button.id}
                  className={`map-right-rail-btn h-10 w-10 border ${
                    button.disabled
                      ? "border-gray-200 bg-gray-50 text-gray-300"
                      : button.isActive
                        ? "map-right-rail-btn-active"
                        : ""
                  }`}
                >
                  <IconComponent size={18} />
                </ActionIconButton>
              </Tooltip>
              {!isMobileViewport && !isLast ? (
                <span
                  aria-hidden="true"
                  className="my-1 block h-px w-7 bg-[#e7edf6]"
                />
              ) : null}
            </div>
          );
        })}
      </div>

      <SmartAssignModal
        isOpen={showSmartAssignModal}
        onClose={() => setShowSmartAssignModal(false)}
      />
    </div>
  );
};

export default MapViewToolbar;

